// ============================================================================
// useAirspaceLayer — renders airspace polygons (TFRs, SUA, Prohibited, etc.)
// on the live Cesium map. Part of Phase 4 item 20.
//
// The `airspace` table is tenant-agnostic (an FAA TFR applies to anyone in
// US airspace) so the fetch doesn't bind to tenantId. We debounce on camera
// movement like useFAAFacilityMap — TFRs are sparse but re-query lets us
// pick up newly-published restrictions without a page refresh.
//
// Color / icon strategy:
//   TFR              → red (highest urgency — active restriction)
//   PROHIBITED       → dark red with hatch border
//   RESTRICTED       → orange
//   WARNING / ALERT  → amber
//   MOA              → purple
//   SUA / other      → steel blue
//
// We render as flat polygons clamped to ground for a clean look — TFR
// altitude floors/ceilings are shown in the click popup rather than as 3D
// extrusions, which would visually compete with drone tracks.
// ============================================================================
import { useEffect, useRef, useCallback } from 'react';
import {
  Viewer as CesiumViewer,
  GeoJsonDataSource,
  ColorMaterialProperty,
  ConstantProperty,
  Color,
  Math as CesiumMath,
  ClassificationType,
  HeightReference,
} from 'cesium';
import { airspaceApi } from '../services/api';

interface AirspaceFeatureProps {
  id: string;
  name: string;
  type: string;
  class?: string | null;
  lowerAltitudeFt: number;
  upperAltitudeFt: number;
  controllingFacility?: string | null;
  effectiveDate: string;
  expirationDate?: string | null;
  notamId?: string | null;
  source: string;
}

// Minimal GeoJSON shape — we don't pull in @types/geojson just for this.
type Position = [number, number] | [number, number, number];
interface PolygonGeometry {
  type: 'Polygon';
  coordinates: Position[][];
}
interface MultiPolygonGeometry {
  type: 'MultiPolygon';
  coordinates: Position[][][];
}
type AnyPolygon = PolygonGeometry | MultiPolygonGeometry;

interface Feature {
  type: 'Feature';
  geometry: AnyPolygon;
  properties: AirspaceFeatureProps;
}

interface FeatureCollection {
  type: 'FeatureCollection';
  features: Feature[];
}

interface AirspaceQueryItem extends AirspaceFeatureProps {
  geometry: AnyPolygon;
}

/** Fetch only when camera is below this height (meters). Above this we'd
 *  pull the entire country's TFR set, which is noisy and pointless on a
 *  continent-scale view. */
const MAX_CAMERA_HEIGHT = 1_500_000; // ~1500 km
const DEBOUNCE_MS = 350;

const COLORS: Record<string, Color> = {
  TFR: Color.fromCssColorString('#ef4444'),
  PROHIBITED: Color.fromCssColorString('#991b1b'),
  RESTRICTED: Color.fromCssColorString('#f97316'),
  WARNING: Color.fromCssColorString('#f59e0b'),
  ALERT: Color.fromCssColorString('#f59e0b'),
  MOA: Color.fromCssColorString('#a855f7'),
  CLASS_B: Color.fromCssColorString('#3b82f6'),
  CLASS_C: Color.fromCssColorString('#3b82f6'),
  CLASS_D: Color.fromCssColorString('#60a5fa'),
  CLASS_E: Color.fromCssColorString('#94a3b8'),
  DEFAULT: Color.fromCssColorString('#64748b'),
};

function colorForType(type: string): Color {
  return COLORS[type] ?? COLORS.DEFAULT;
}

function stylePolygons(ds: GeoJsonDataSource): number {
  const entities = ds.entities.values;
  for (const entity of entities) {
    const type = (entity.properties?.type?.getValue() as string) || 'DEFAULT';
    const base = colorForType(type);
    if (entity.polygon) {
      entity.polygon.material = new ColorMaterialProperty(base.withAlpha(0.3));
      entity.polygon.outline = new ConstantProperty(true);
      entity.polygon.outlineColor = new ConstantProperty(base.withAlpha(0.95));
      entity.polygon.outlineWidth = new ConstantProperty(2);
      // Clamp to ground so the polygon drapes over terrain and we don't
      // have to fiddle with extrusion heights. Click-through still works
      // because Cesium's picker fires on the ground-clamped primitive.
      entity.polygon.heightReference = new ConstantProperty(
        HeightReference.CLAMP_TO_GROUND,
      );
      entity.polygon.classificationType = new ConstantProperty(
        ClassificationType.BOTH,
      );
    }
  }
  return entities.length;
}

function toFeatureCollection(items: AirspaceQueryItem[]): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: items.map((item) => ({
      type: 'Feature',
      geometry: item.geometry,
      properties: {
        id: item.id,
        name: item.name,
        type: item.type,
        class: item.class ?? null,
        lowerAltitudeFt: item.lowerAltitudeFt,
        upperAltitudeFt: item.upperAltitudeFt,
        controllingFacility: item.controllingFacility ?? null,
        effectiveDate: item.effectiveDate,
        expirationDate: item.expirationDate ?? null,
        notamId: item.notamId ?? null,
        source: item.source,
      } as AirspaceFeatureProps,
    })),
  };
}

export function useAirspaceLayer(
  viewer: CesiumViewer | null,
  visible: boolean,
) {
  const dataSourceRef = useRef<GeoJsonDataSource | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewerRef = useRef<CesiumViewer | null>(null);

  const loadForViewport = useCallback(
    async (v: CesiumViewer) => {
      if (v.isDestroyed()) return;
      const rect = v.camera.computeViewRectangle();
      if (!rect) return;
      const height = v.camera.positionCartographic.height;
      if (height > MAX_CAMERA_HEIGHT) {
        if (dataSourceRef.current) {
          dataSourceRef.current.entities.removeAll();
        }
        return;
      }

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await airspaceApi.query({
          minLat: CesiumMath.toDegrees(rect.south),
          maxLat: CesiumMath.toDegrees(rect.north),
          minLng: CesiumMath.toDegrees(rect.west),
          maxLng: CesiumMath.toDegrees(rect.east),
        });

        if (controller.signal.aborted || v.isDestroyed()) return;

        const items = (res?.data ?? []) as AirspaceQueryItem[];
        const geojson = toFeatureCollection(items);

        let ds = dataSourceRef.current;
        if (!ds) {
          ds = new GeoJsonDataSource('airspace');
          dataSourceRef.current = ds;
          if (!v.isDestroyed()) {
            await v.dataSources.add(ds);
          }
        }
        if (v.isDestroyed()) return;

        await ds.load(geojson, { clampToGround: true });
        if (v.isDestroyed()) return;

        stylePolygons(ds);
        ds.show = visible;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Don't spam the console — most failures here are transient (auth
        // race on first paint, etc.). Log once per error message.
        console.error('[Airspace Layer] load failed', err);
      }
    },
    [visible],
  );

  // Camera listener + initial fetch.
  useEffect(() => {
    if (!viewer || viewer.isDestroyed()) return;
    viewerRef.current = viewer;

    if (!dataSourceRef.current) {
      const ds = new GeoJsonDataSource('airspace');
      dataSourceRef.current = ds;
      ds.show = visible;
      viewer.dataSources.add(ds);
    }

    const onCameraChange = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (viewer && !viewer.isDestroyed() && visible) {
          loadForViewport(viewer);
        }
      }, DEBOUNCE_MS);
    };
    viewer.camera.changed.addEventListener(onCameraChange);

    let initialTimer: ReturnType<typeof setTimeout> | null = null;
    if (visible) {
      initialTimer = setTimeout(() => {
        if (!viewer.isDestroyed() && visible) loadForViewport(viewer);
      }, 500);
    }

    return () => {
      if (initialTimer) clearTimeout(initialTimer);
      if (!viewer.isDestroyed()) {
        viewer.camera.changed.removeEventListener(onCameraChange);
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [viewer, visible, loadForViewport]);

  // Toggle visibility without re-fetching.
  useEffect(() => {
    if (dataSourceRef.current) dataSourceRef.current.show = visible;
  }, [visible]);

  // Unmount cleanup.
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (dataSourceRef.current) {
        try {
          const v = viewerRef.current;
          if (v && !v.isDestroyed()) {
            v.dataSources.remove(dataSourceRef.current, true);
          }
        } catch {
          /* viewer may already be destroyed */
        }
        dataSourceRef.current = null;
        viewerRef.current = null;
      }
    };
  }, []);
}
